import type {FlatRow} from '@/modules/patients/components/patient-details/therapy/tables/rows'

// Only prescriptions and guideline notes are selectable; their note rows follow the prescription.
// A sequence with two medicines has two rows, so the ids are unique.
export function getSelectableRowIds(flatRows: readonly FlatRow[]): string[] {
  const ids = new Set<string>()
  for (const row of flatRows) {
    if (row.type === 'therapy' || row.type === 'note') ids.add(row.therapy.id)
  }
  return [...ids]
}

export function getDayRowIds(flatRows: readonly FlatRow[], dateKey: string): string[] {
  const ids = new Set<string>()
  let inDay = false

  for (const row of flatRows) {
    if (row.type === 'date-header') {
      inDay = row.dateKey === dateKey
      continue
    }
    if (inDay && (row.type === 'therapy' || row.type === 'note')) ids.add(row.therapy.id)
  }

  return [...ids]
}

export function pruneSelection(
  selectedIds: ReadonlySet<string>,
  selectableIds: readonly string[]
): ReadonlySet<string> {
  const selectable = new Set(selectableIds)
  let changed = false

  for (const id of selectedIds) {
    if (!selectable.has(id)) {
      changed = true
      break
    }
  }

  return changed ? new Set([...selectedIds].filter(id => selectable.has(id))) : selectedIds
}

/**
 * A plain click toggles the row and makes it the anchor. A shift-click from the anchor selects (or clears) the
 * range up to the row and ends the range, so the next shift-click starts a new selection on its own row
 * instead of extending the old one.
 */
export function toggleRowSelection(
  selectedIds: ReadonlySet<string>,
  anchorId: string | null,
  targetId: string,
  orderedIds: readonly string[],
  shift = false
): {ids: ReadonlySet<string>; anchorId: string | null} {
  if (shift) {
    const anchorIndex = anchorId ? orderedIds.indexOf(anchorId) : -1
    const targetIndex = orderedIds.indexOf(targetId)
    if (anchorId !== targetId && anchorIndex >= 0 && targetIndex >= 0) {
      const [from, to] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
      const next = new Set(selectedIds)
      const shouldSelect = !selectedIds.has(targetId)
      for (const id of orderedIds.slice(from, to + 1)) {
        if (shouldSelect) next.add(id)
        else next.delete(id)
      }
      return {ids: next, anchorId: null}
    }
    // no range to complete: the selection starts over on this row
    return {ids: new Set([targetId]), anchorId: targetId}
  }

  const next = new Set(selectedIds)
  if (next.has(targetId)) next.delete(targetId)
  else next.add(targetId)

  return {ids: next, anchorId: targetId}
}

// Selects every id when some are missing, otherwise clears the whole group (the header and day checkboxes).
export function toggleSelectionGroup(
  selectedIds: ReadonlySet<string>,
  groupIds: readonly string[]
): ReadonlySet<string> {
  const next = new Set(selectedIds)
  const allSelected = groupIds.length > 0 && groupIds.every(id => next.has(id))

  for (const id of groupIds) {
    if (allSelected) next.delete(id)
    else next.add(id)
  }

  return next
}

export function getGroupSelectionState(
  selectedIds: ReadonlySet<string>,
  groupIds: readonly string[]
): boolean | 'indeterminate' {
  if (groupIds.length === 0) return false
  const selectedCount = groupIds.filter(id => selectedIds.has(id)).length
  if (selectedCount === 0) return false
  return selectedCount === groupIds.length ? true : 'indeterminate'
}

function getPrintRowTherapyId(row: FlatRow): string | undefined {
  switch (row.type) {
    case 'therapy':
    case 'note':
      return row.therapy.id
    case 'medicine-note':
      return row.therapyId
    default:
      return undefined
  }
}

export function buildPrintRows(flatRows: readonly FlatRow[], selectedIds: ReadonlySet<string>): FlatRow[] {
  if (selectedIds.size === 0) return [...flatRows]

  const selectedTherapies = new Set<string>()
  for (const row of flatRows) {
    const therapyId = getPrintRowTherapyId(row)
    if (therapyId != null && selectedIds.has(therapyId)) selectedTherapies.add(therapyId)
  }

  const rows: FlatRow[] = []
  let dateHeader: FlatRow | null = null
  let headerAdded = false

  for (const row of flatRows) {
    if (row.type === 'date-header') {
      dateHeader = row
      headerAdded = false
      continue
    }

    const therapyId = getPrintRowTherapyId(row)
    if (therapyId == null || !selectedTherapies.has(therapyId)) continue

    if (dateHeader && !headerAdded) {
      rows.push(dateHeader)
      headerAdded = true
    }
    rows.push(row)
  }

  return rows
}
